import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import axios from 'axios';
import { format } from 'date-fns';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { CurrentUser } from '../../axios';
import { taskAssignsApi } from '../../axios/taskAssigns';
import { useFeatureFlags, useLanguage } from '../../hooks';
import { useUserDataStore } from '../../newStore/userDataStore';
import { TASK_ASSIGNER_FILTERS_NAME } from '../../types';
import type { TaskAssignerSortBy } from '../../types';
import {
  GetTaskAssignsResponse,
  NameAndIdElement,
  TaskAssign,
  TaskAssignerMobileUserElement,
} from '../../types/taskAssigns';
import { datascopeTracker } from '../../utils/eventTracker';
import { redirectTo } from '../../utils/navigation';
import { FEATURE_NOT_IN_PLAN } from '../../utils/pricingErrorCodes';
import { Sorter, useTaskAssignerFilterStore } from './TaskAssignerFilterStore';
import { trackTaskAssignerViewList } from './taskAssignerAnalytics';

export type TaskAssignerFiltersLocationEntry = {
  label: string;
  value: number;
  type: 'NestableLocation' | 'Location';
};

export interface FiltersState {
  userId?: number;
  /**
   * Selected place filter(s). Same shape for single- and multi-select:
   * one entry = single selection; multiple = multiselect. Omitted when no location filter.
   */
  locations?: TaskAssignerFiltersLocationEntry[];
  taskFormId?: number;
  state?: string;
  taskAssignID?: string;
  startDate?: { start: string; end: string };
}

interface TaskAssignerContextType {
  tasks: TaskAssign[];
  totalTasks: number;
  totalScheduledTasks: number;
  totalCompletedTasks: number;
  totalPendingTasks: number;
  totalIncompletedTasks: number;
  totalSentTasks: number;
  totalRejectedTasks: number;
  page: number;
  setPage: (page: number) => void;
  loading: boolean;
  locations: NameAndIdElement[];
  forms: NameAndIdElement[];
  mobileUsers: TaskAssignerMobileUserElement[];
  tabIndex: number;
  setTabIndex: (newValue: number) => void;
  changeTab: (newValue: number) => void;
  getData: (newPage?: number) => void;
  selectedTaskIds: number[];
  setSelectedTaskIds: (ids: number[]) => void;
  setSelectedTasksUserIds: (ids: number[]) => void;
  selectedTasksUserIds: number[];
  allTasksSelected: boolean;
  updateOrder: (newSortBy: TaskAssignerSortBy) => void;
  sorter: Sorter;
  filtersApplied: number;
  filtersState: FiltersState;
  linkExport: (filename?: string) => void;
  setRefresh: (refresh: boolean) => void;
  userDataLoading: boolean;
  permissions: string[];
  currentUser: CurrentUser;
  hasMore: boolean;
  hasLess: boolean;
  // Whether the viewer can manage group tasks. When false (operario), group
  // rows only expose "respond"; detail/notify/edit/delete are hidden.
  canManageGroupTasks: boolean;
  // Account's plan doesn't include this module (backend 403 `feature-not-in-plan`).
  planAccessDenied: boolean;
  // False until the first getData() resolves. Guard rendering on this:
  // planAccessDenied only flips true async (on 403), so without it a blocked
  // account flashes the unblocked module while the first request is in flight.
  planAccessChecked: boolean;
}

const TaskAssignerContext = createContext<TaskAssignerContextType | null>(null);

// True when the account's plan doesn't include this module (backend
// TaskAssignsPricing gate, 403 `feature-not-in-plan`). Expected flow for
// blocked accounts -- callers should skip console.error for this case.
export const isPlanAccessDeniedError = (error: unknown): boolean => {
  const errorCode = axios.isAxiosError(error)
    ? (error.response?.data as { error?: string } | undefined)?.error
    : undefined;
  return axios.isAxiosError(error) && error.response?.status === 403 && errorCode === FEATURE_NOT_IN_PLAN;
};

const resolveLocationType = (
  type: string | null | undefined,
): 'NestableLocation' | 'Location' | undefined => {
  if (type === 'nestable_locations') return 'NestableLocation';
  if (type === 'locations') return 'Location';
  return undefined;
};

export const TaskAssignerProvider = ({
  children,
  gateOnly = false,
}: {
  children: ReactNode;
  // When true, this provider is mounted only to gate a non-list route
  // (create/edit/groupEdit) on plan access. It runs a single lightweight
  // plan-access probe instead of the full list fetch, avoiding an unnecessary
  // getTasks + getTasksBoxes load and a false "viewed task list" analytics
  // event on every create/edit page load.
  gateOnly?: boolean;
}) => {
  const { currentUser, permissions, loading: userDataLoading } = useUserDataStore();
  const { checkFlag } = useFeatureFlags();
  const lang = useLanguage();
  const multiSelectLocationEnabled = checkFlag('TaskAssignerFilterMultiSelectLocation');
  const multiSelectStatusEnabled = checkFlag('TaskAssignerFilterMultiSelectStatus');
  const multiSelectUserEnabled = checkFlag('TaskAssignerFilterMultiSelectUser');
  const [tasks, setTasks] = useState([]);
  // Default true: older payloads (and non-group accounts) behave as before,
  // i.e. the viewer manages group tasks. The backend sends false for operarios.
  const [canManageGroupTasks, setCanManageGroupTasks] = useState(true);
  const [totalTasks, setTotalTasks] = useState(null);
  const [totalScheduledTasks, setTotalScheduledTasks] = useState(0);
  const [totalCompletedTasks, setTotalCompletedTasks] = useState(0);
  const [totalPendingTasks, setTotalPendingTasks] = useState(0);
  const [totalIncompletedTasks, setTotalIncompletedTasks] = useState(0);
  const [totalSentTasks, setTotalSentTasks] = useState(0);
  const [totalRejectedTasks, setTotalRejectedTasks] = useState(0);

  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState<number>(0);

  const [locations, setLocations] = useState<NameAndIdElement[]>([]);
  const [forms, setForms] = useState<NameAndIdElement[]>([]);
  const [mobileUsers, setMobileUsers] = useState<TaskAssignerMobileUserElement[]>([]);

  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [selectedTasksUserIds, setSelectedTasksUserIds] = useState<number[]>([]);

  const [refresh, setRefresh] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [hasLess, setHasLess] = useState(false);
  const [planAccessDenied, setPlanAccessDenied] = useState(false);
  const [planAccessChecked, setPlanAccessChecked] = useState(false);

  const { filters, currentPage, sorter, searchQuery } = useStoreWithEqualityFn(
    useTaskAssignerFilterStore,
    (state) => ({
      filters: state.filters,
      currentPage: state.currentPage,
      sorter: state.sorter,
      searchQuery: state.searchQuery,
    }),
    shallow,
  );
  const { setCurrentPage, setSorter } = useTaskAssignerFilterStore((state) => state.actions);

  const hasTrackedViewListRef = useRef(false);
  // Mount order means this provider's own fetch effect (a descendant) runs
  // before TaskAssigner.tsx's URL-hydration effect (its ancestor) -- so a
  // "Ver todos" deep link always fires one getData() call with no location
  // filter yet, immediately followed by a second, correctly-filtered one once
  // the hydration effect applies it. Neither getData() call cancels the
  // other, so without this the two responses resolve by arrival order, not by
  // request order -- and the unfiltered query (a wider scan of every task in
  // the account) reliably takes longer than the filtered one, so it lands
  // last and silently overwrites the correct result. Same race DS-4023 fixed
  // for the Users list; same fix shape, adapted to this component's own
  // state instead of a Redux slice.
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    if (gateOnly) return;
    getMetadata();
  }, [multiSelectUserEnabled, gateOnly]);

  // gateOnly: mounted around create/edit routes purely to detect the pricing
  // 403 so PlanAccessGate can render the landing. One lightweight gated request
  // -- no list/stats fetch, no "viewed list" analytics -- unlike the list view.
  useEffect(() => {
    if (!gateOnly) return;
    checkPlanAccess();
  }, [gateOnly]);

  useEffect(() => {
    if (gateOnly) return;
    setLoading(true);
    // Reset selection so the bulk-action guard always validates against the visible page.
    setSelectedTaskIds([]);
    setSelectedTasksUserIds([]);
    getData();
  }, [
    filters,
    currentPage,
    sorter,
    searchQuery,
    refresh,
    multiSelectLocationEnabled,
    multiSelectStatusEnabled,
    multiSelectUserEnabled,
    gateOnly,
  ]);

  async function getData() {
    const requestId = ++latestRequestIdRef.current;
    const isStale = () => requestId !== latestRequestIdRef.current;

    const dateRange = filters[TASK_ASSIGNER_FILTERS_NAME.DATE_RANGE];
    const locationIds = filters[TASK_ASSIGNER_FILTERS_NAME.LOCATION];
    const locationType = filters[TASK_ASSIGNER_FILTERS_NAME.LOCATION_TYPE];
    const formIds = filters[TASK_ASSIGNER_FILTERS_NAME.FORM];
    const statusValues = filters[TASK_ASSIGNER_FILTERS_NAME.STATUS];
    const userIds = filters[TASK_ASSIGNER_FILTERS_NAME.USER];
    const groupIds = filters[TASK_ASSIGNER_FILTERS_NAME.GROUP];
    const groupTaskAssignEnabled = checkFlag('GroupTaskAssign');
    const effectiveGroupIds =
      groupTaskAssignEnabled && groupIds && groupIds.length > 0 ? groupIds : undefined;
    const taskId = filters[TASK_ASSIGNER_FILTERS_NAME.TASK_ID];
    const assetFilter = filters[TASK_ASSIGNER_FILTERS_NAME.ASSET];
    const origin = filters[TASK_ASSIGNER_FILTERS_NAME.ORIGIN];
    const assetId = assetFilter?.assets?.length
      ? assetFilter.assets.map(String).join(',')
      : undefined;
    const assetTypeIds = assetFilter?.asset_types?.length
      ? assetFilter.asset_types.map(String).join(',')
      : undefined;

    const { sortBy, order } = sorter;

    const multiFormFilter = checkFlag('TaskAssignerFilterMultiSelectForm');
    const requestParams = {
      page: currentPage + 1,
      taskAssignID: taskId ?? undefined,
      ...(multiSelectUserEnabled
        ? {
            mobileUserIds: userIds?.length ? userIds : undefined,
            userId: undefined,
            useMobileUserIdsQueryParam: true,
          }
        : {
            userId: userIds?.[0] ?? undefined,
            mobileUserIds: undefined,
            useMobileUserIdsQueryParam: false,
          }),
      groupIds: effectiveGroupIds,
      ...(multiSelectLocationEnabled
        ? {
            locationIds: locationIds?.length ? locationIds : undefined,
            locationId: undefined,
          }
        : {
            locationId: locationIds?.[0] ?? undefined,
            locationIds: undefined,
          }),
      locationType: resolveLocationType(locationType),
      taskFormId: multiFormFilter ? undefined : formIds?.[0] ?? undefined,
      taskFormIds: multiFormFilter && formIds?.length ? formIds : undefined,
      states: multiSelectStatusEnabled && statusValues?.length ? statusValues : undefined,
      state: multiSelectStatusEnabled ? undefined : statusValues?.[0] ?? undefined,
      startDate: dateRange?.from ? format(dateRange.from, 'dd/MM/yyyy') : undefined,
      endDate: dateRange?.to ? format(dateRange.to, 'dd/MM/yyyy') : undefined,
      sortBy,
      order,
      searchFormName: searchQuery || undefined,
      assetId,
      assetTypeIds,
      origin: origin ?? undefined,
    };
    try {
      const boxesResponse = taskAssignsApi.getTasksBoxes(requestParams);
      // getTasksBoxes fires here in parallel, but getTasks (below) is awaited
      // first. If getTasks rejects (e.g. a plan-blocked account 403s on both
      // endpoints) we jump straight to the catch and never await boxesResponse
      // -- its rejection would then surface as an "unhandled promise rejection"
      // in the console. This no-op catch just marks it handled; the real value
      // (or error) is still read where boxesResponse is awaited further down.
      boxesResponse.catch((): void => undefined);
      const lightPagination = checkFlag('TurnOffTaskAssignStatistics');
      const {
        tasks: responseTasks,
        has_more,
        has_less,
        can_manage_group_tasks,
      }: GetTaskAssignsResponse = await taskAssignsApi.getTasks(requestParams);

      // A newer getData() started while this one was in flight -- its own
      // response (or one it already applied) is the one that should stick.
      if (isStale()) return;

      // Only clear a plan-access denial once we have positive confirmation of
      // access (getTasks resolved). Clearing it eagerly at the top of this
      // function meant every getData() rerun -- e.g. once the feature flags
      // in the effect's deps finish loading async, re-triggering it -- would
      // flash the unblocked module for a blocked account until this request
      // round-tripped and the catch below re-set it.
      setPlanAccessDenied(false);
      setTasks(responseTasks ?? []);
      setCanManageGroupTasks(can_manage_group_tasks ?? true);
      setLoading(false);
      if (lightPagination) {
        setHasMore(has_more);
        setHasLess(has_less);
      }

      try {
        const {
          completed,
          in_progress: pendingTasks,
          incomplete: incompletedTasks,
          sent: sentTasks,
          rejected: rejectedTasks,
          scheduled: scheduledTasks,
          assigns,
        } = await boxesResponse;

        if (isStale()) return;

        if (!lightPagination) {
          // When ScheduledTaskSend is off, scheduled tasks shouldn't be counted in the total
          // (the "Scheduled" stat card is hidden under the same flag in TaskAssignerHeader).
          const scheduledSendEnabled = checkFlag('ScheduledTaskSend');
          const scheduledCount = scheduledTasks ?? 0;
          setTotalTasks(scheduledSendEnabled ? assigns : assigns - scheduledCount);
        }
        setTotalScheduledTasks(scheduledTasks ?? 0);

        if (currentUser?.company_id && !hasTrackedViewListRef.current) {
          hasTrackedViewListRef.current = true;
          trackTaskAssignerViewList(currentUser.company_id, scheduledTasks ?? 0);
        }
        setTotalCompletedTasks(completed);
        setTotalPendingTasks(pendingTasks);
        setTotalIncompletedTasks(incompletedTasks);
        setTotalSentTasks(sentTasks);
        setTotalRejectedTasks(rejectedTasks);
      } catch (boxesError) {
        // getTasks already succeeded above, so the account does have access --
        // a boxes-only failure (e.g. a stray 403) must not flip
        // planAccessDenied or hide the task list that's already rendering.
        if (!isPlanAccessDeniedError(boxesError)) {
          console.error('error', boxesError);
        }
      }
    } catch (error) {
      // A stale rejection is the worse of the two outcomes to apply here: it
      // would empty the list and raise a plan-access error nobody is waiting
      // on, for a request a newer one has already superseded.
      if (!isStale()) {
        if (isPlanAccessDeniedError(error)) {
          setPlanAccessDenied(true);
        } else {
          console.error('error', error);
        }
        setLoading(false);
      }
    } finally {
      setPlanAccessChecked(true);
    }
  }

  // Lightweight counterpart to getData() for gateOnly mode: hits the same
  // plan-gated endpoint just to learn whether the account has access, without
  // loading the list, fetching stats, or emitting the view-list analytics event.
  async function checkPlanAccess() {
    try {
      await taskAssignsApi.getTasks({ page: 1 });
      setPlanAccessDenied(false);
    } catch (error) {
      if (isPlanAccessDeniedError(error)) {
        setPlanAccessDenied(true);
      } else {
        console.error('error', error);
      }
    } finally {
      setPlanAccessChecked(true);
    }
  }

  async function getMetadata() {
    try {
      const { mobile_users, locations, forms } = await taskAssignsApi.getLightFilterMetadata({
        includeDeactivatedUsers: multiSelectUserEnabled,
      });
      setMobileUsers(
        mobile_users.map((entry) => ({
          label: entry[0],
          id: entry[1],
          ...(entry.length > 2 ? { isDeactivated: entry[2] } : {}),
        })),
      );
      setLocations(
        locations.map((entry) => ({
          label: entry[0],
          id: entry[1],
        })),
      );
      setForms(
        forms.map((entry) => ({
          label: entry[0],
          id: entry[1],
        })),
      );
    } catch (error) {
      if (!isPlanAccessDeniedError(error)) {
        console.error('error', error);
      }
    }
  }

  const changeTab = (newValue: number) => {
    datascopeTracker(
      newValue === 0 ? 'Click on Task View' : 'Click on Timeline View',
      { source: 'TASK_ASSIGNER' },
      'AMP',
    );
    setTabIndex(newValue);
  };

  const filtersApplied = Object.entries(filters).reduce((acc, [key, value]) => {
    if (key === TASK_ASSIGNER_FILTERS_NAME.DATE_RANGE) return acc;
    if (key === TASK_ASSIGNER_FILTERS_NAME.LOCATION_TYPE) return acc;
    if (!value) return acc;
    if (Array.isArray(value) && value.length > 0) return acc + 1;
    if (typeof value === 'string' && value !== '') return acc + 1;
    return acc;
  }, 0);

  const page = currentPage;

  const updateOrder = async (newSortBy: TaskAssignerSortBy) => {
    const newSorter: Sorter = { sortBy: newSortBy, order: 'asc' };
    if (sorter.sortBy === newSortBy && sorter.order === 'asc') newSorter.order = 'desc';
    setSorter(newSorter);
    if (page !== 0) {
      setCurrentPage(0);
    }
  };

  const allTasksSelected = selectedTaskIds.length === tasks?.length;

  const linkExport = (filename?: string) => {
    const exportUrl = new URL(`${process.env.REACT_APP_BACKEND_ENDPOINT}/task_assigns.xlsx`);

    if (filename) exportUrl.searchParams.append('filename', filename);
    // Force the backend to resolve I18n.locale from the user's current UI
    // language instead of falling back to Account#language. Mirrors the
    // pattern already used in other backend redirects (Login, Iframe,
    // AccountAdvancedConfig).
    exportUrl.searchParams.append('loc', lang.lang);

    const dateRange = filters[TASK_ASSIGNER_FILTERS_NAME.DATE_RANGE];
    const locationIds = filters[TASK_ASSIGNER_FILTERS_NAME.LOCATION];
    const locationType = filters[TASK_ASSIGNER_FILTERS_NAME.LOCATION_TYPE];
    const formIds = filters[TASK_ASSIGNER_FILTERS_NAME.FORM];
    const statusValues = filters[TASK_ASSIGNER_FILTERS_NAME.STATUS];
    const userIds = filters[TASK_ASSIGNER_FILTERS_NAME.USER];
    const groupIds = filters[TASK_ASSIGNER_FILTERS_NAME.GROUP];
    const taskId = filters[TASK_ASSIGNER_FILTERS_NAME.TASK_ID];

    if (taskId) exportUrl.searchParams.append('assign_id', taskId);
    if (multiSelectUserEnabled && userIds && userIds.length > 0) {
      userIds.forEach((id) => exportUrl.searchParams.append('mobile_user_ids[]', id.toString()));
    } else if (userIds?.[0]) {
      exportUrl.searchParams.append('user_id', userIds[0].toString());
    }
    // Group filter: surfaces group task assigns whose destination group
    // is among the selected ids. Backend hides individual rows whenever
    // any `group_ids` value is present, matching the supervisor's intent
    // of "show me only the group tasks targeting these groups". Gated
    // by the same FF as the backend honoring it, so accounts without
    // the feature stay on the legacy contract.
    const groupTaskAssignEnabled = checkFlag('GroupTaskAssign');
    if (groupTaskAssignEnabled && groupIds && groupIds.length > 0) {
      groupIds.forEach((id) => exportUrl.searchParams.append('group_ids[]', id.toString()));
    }
    if (multiSelectLocationEnabled && locationIds && locationIds.length > 0) {
      locationIds.forEach((id) => exportUrl.searchParams.append('location_ids[]', id.toString()));
      const resolvedType = resolveLocationType(locationType);
      if (resolvedType) exportUrl.searchParams.append('location_type', resolvedType);
    } else if (locationIds?.[0]) {
      exportUrl.searchParams.append('location_id', locationIds[0].toString());
      const resolvedType = resolveLocationType(locationType);
      if (resolvedType) exportUrl.searchParams.append('location_type', resolvedType);
    }
    const multiFormFilter = checkFlag('TaskAssignerFilterMultiSelectForm');
    if (multiFormFilter && formIds?.length) {
      formIds.forEach((id) => exportUrl.searchParams.append('task_form_ids[]', id.toString()));
    } else if (formIds?.[0]) {
      exportUrl.searchParams.append('task_form_id', formIds[0].toString());
    }
    if (multiSelectStatusEnabled && statusValues?.length) {
      statusValues.forEach((value) => exportUrl.searchParams.append('states[]', value));
    } else if (statusValues?.[0]) {
      exportUrl.searchParams.append('state', statusValues[0]);
    }
    if (dateRange?.from) exportUrl.searchParams.append('start', format(dateRange.from, 'dd/MM/yyyy'));
    if (dateRange?.to) exportUrl.searchParams.append('end', format(dateRange.to, 'dd/MM/yyyy'));

    redirectTo(exportUrl.toString(), true);
  };

  const locationFilterIds = filters[TASK_ASSIGNER_FILTERS_NAME.LOCATION];
  const resolvedLocationType =
    resolveLocationType(filters[TASK_ASSIGNER_FILTERS_NAME.LOCATION_TYPE]) ?? 'Location';
  const selectedLocationsForState: TaskAssignerFiltersLocationEntry[] | undefined =
    locationFilterIds && locationFilterIds.length > 0
      ? locationFilterIds.map((value) => ({
          value,
          label: '',
          type: resolvedLocationType,
        }))
      : undefined;

  const filtersState: FiltersState = {
    userId: filters[TASK_ASSIGNER_FILTERS_NAME.USER]?.[0] ?? undefined,
    taskFormId: filters[TASK_ASSIGNER_FILTERS_NAME.FORM]?.[0] ?? undefined,
    state: filters[TASK_ASSIGNER_FILTERS_NAME.STATUS]?.[0] ?? undefined,
    taskAssignID: filters[TASK_ASSIGNER_FILTERS_NAME.TASK_ID] ?? undefined,
    startDate: {
      start: filters[TASK_ASSIGNER_FILTERS_NAME.DATE_RANGE]?.from
        ? format(filters[TASK_ASSIGNER_FILTERS_NAME.DATE_RANGE].from, 'dd/MM/yyyy')
        : '',
      end: filters[TASK_ASSIGNER_FILTERS_NAME.DATE_RANGE]?.to
        ? format(filters[TASK_ASSIGNER_FILTERS_NAME.DATE_RANGE].to, 'dd/MM/yyyy')
        : '',
    },
    locations: selectedLocationsForState,
  };

  const taskAssignsValue = {
    tasks,
    totalTasks,
    totalScheduledTasks,
    totalCompletedTasks,
    totalPendingTasks,
    totalIncompletedTasks,
    page,
    setPage: setCurrentPage,
    loading,
    locations,
    forms,
    mobileUsers,
    tabIndex,
    setTabIndex,
    changeTab,
    getData,
    selectedTaskIds,
    setSelectedTaskIds,
    setSelectedTasksUserIds,
    selectedTasksUserIds,
    allTasksSelected,
    updateOrder,
    sorter,
    filtersApplied,
    filtersState,
    linkExport,
    setRefresh,
    totalSentTasks,
    totalRejectedTasks,
    userDataLoading,
    permissions,
    currentUser,
    hasMore,
    hasLess,
    canManageGroupTasks,
    planAccessDenied,
    planAccessChecked,
  };

  return (
    <TaskAssignerContext.Provider value={taskAssignsValue}>{children}</TaskAssignerContext.Provider>
  );
};

export const useTaskAssignerContext = () => useContext(TaskAssignerContext);
